// Mensajes de autenticación localizados y compartidos entre LoginView y
// RegisterView: la política de contraseña (checklist en vivo) y el mapeo de
// errores de Cognito a textos claros. Centralizar aquí evita la duplicación que
// existía (cada vista tenía su propia copia en español) y garantiza que ambas
// se traduzcan con las mismas claves (i18n).

import { useTranslation } from 'react-i18next';
import { CognitoError } from './cognito.js';

/** Una regla de la política de contraseña evaluada en vivo. */
export interface PasswordRule {
  label: string;
  ok: boolean;
}

export interface AuthMessages {
  /**
   * Reglas de robustez de contraseña (política AWS Cognito), ya traducidas.
   * `variant` ajusta el texto: `login` usa rótulos largos (como la vista de
   * login histórica); `register` usa rótulos cortos.
   */
  passwordPolicy: (pw: string, variant?: 'login' | 'register') => PasswordRule[];
  /** Traduce un error de registro (sign-up / confirmación) a texto claro. */
  signUpError: (err: unknown) => string;
  /** Traduce un error de login / recuperación a texto claro. */
  loginError: (err: unknown) => string;
}

/** Hook con los mensajes de autenticación localizados. */
export function useAuthMessages(): AuthMessages {
  const { t } = useTranslation();

  const passwordPolicy = (pw: string, variant: 'login' | 'register' = 'register'): PasswordRule[] => [
    {
      label: variant === 'login' ? t('auth.password.min8Alnum') : t('auth.password.min8'),
      ok: pw.length >= 8,
    },
    {
      label:
        variant === 'login'
          ? t('auth.password.upperAndNumberLong')
          : t('auth.password.upperAndNumber'),
      ok: /[A-Z]/.test(pw) && /\d/.test(pw),
    },
    { label: t('auth.password.symbol'), ok: /[!@#$&*_-]/.test(pw) },
  ];

  const signUpError = (err: unknown): string => {
    if (err instanceof CognitoError) {
      switch (err.type) {
        case 'UsernameExistsException':
          return t('auth.errors.usernameExists');
        case 'InvalidPasswordException':
          return t('auth.errors.invalidPassword');
        case 'InvalidParameterException':
          return t('auth.errors.invalidParameter');
        case 'NotAuthorizedException':
          return t('auth.errors.notAuthorizedSignup');
        case 'TooManyRequestsException':
        case 'LimitExceededException':
          return t('auth.errors.tooManyRequests');
        default:
          return err.message;
      }
    }
    return t('auth.errors.signupGeneric');
  };

  const loginError = (err: unknown): string => {
    if (err instanceof CognitoError) {
      switch (err.type) {
        case 'NotAuthorizedException':
          return t('auth.errors.notAuthorizedLogin');
        case 'UserNotConfirmedException':
          return t('auth.errors.userNotConfirmed');
        case 'CodeMismatchException':
          return t('auth.errors.codeMismatch');
        case 'InvalidPasswordException':
          return t('auth.errors.invalidPasswordLogin');
        case 'LimitExceededException':
          return t('auth.errors.limitExceeded');
        default:
          return err.message;
      }
    }
    return err instanceof Error ? err.message : t('auth.errors.unexpected');
  };

  return { passwordPolicy, signUpError, loginError };
}
